import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/react";
import App from "@app/App";
import { store } from "@/store/store";
import "./index.css";
import "@/components/AIAssistant/styles/variables.scss";
import { AblyProvider } from "./ably/AblyContext";
import { AuthProvider } from "@/auth/AuthProvider";
import CONFIG from "./config";

const isSentryEnabled = CONFIG.sentry.isSentryEnabled;
const sentryDsn = CONFIG.sentry.sentryDsn;

if (!sentryDsn || isSentryEnabled === "false") {
    if (import.meta.env.DEV) {
        console.warn("Sentry Not Configured");
    }
} else {
    Sentry.init({
        dsn: sentryDsn,
        release: `v${import.meta.env.VITE_VERSION}`,
        environment: CONFIG.sentry.sentryEnvironment || import.meta.env.MODE,
        tracesSampleRate: Number(CONFIG.sentry.sentrySampleRate) || 0.2,
        replaysSessionSampleRate: Number(CONFIG.sentry.sentryReplaysSsRate) || 0.2,
        replaysOnErrorSampleRate: Number(CONFIG.sentry.sentryReplaysErrorSsRate) || 0.2,
        tracePropagationTargets: [
            "localhost",
            "https://.*.theharrispoll.dev/.*",
        ],
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        beforeSend(event, hint) {
            if (event.request?.headers?.["Authorization"] && event.exception) {
                const error = hint.originalException;
                if (
                    error &&
                    typeof error === "object" &&
                    "response" in error &&
                    error.response &&
                    typeof error.response === "object" &&
                    "status" in error.response &&
                    error.response.status === 401
                ) {
                    return null;
                }
            }

            if (import.meta.env.DEV) {
                const error = hint.originalException;
                if (
                    error &&
                    typeof error === "object" &&
                    "message" in error &&
                    typeof error.message === "string" &&
                    (error.message.includes("Failed to fetch") ||
                        error.message.includes("NetworkError") ||
                        error.message.includes("Network request failed"))
                ) {
                    const url = event.request?.url || "";
                    if (url.includes("localhost") || url.includes("127.0.0.1")) {
                        return null;
                    }
                }
            }

            const error = hint.originalException;
            if (error && typeof error === "object" && "message" in error) {
                const errorMessage = String(error.message).toLowerCase();
                const ignoredPatterns = [
                    "resizeobserver loop limit exceeded",
                    "non-error promise rejection captured",
                    "script error",
                ];

                if (ignoredPatterns.some((pattern) => errorMessage.includes(pattern))) {
                    return null;
                }
            }

            // Add additional context
            if (event.contexts) {
                event.contexts.runtime = {
                    name: "browser",
                    version: navigator.userAgent,
                };
            }

            return event;
        },
    });
}

// Handle Vite preload errors (e.g., when a new deployment happens and old chunks are requested)
const VITE_PRELOAD_RELOAD_FLAG = "vite:preloadError:reloadAttempted";
window.addEventListener(
    "vite:preloadError",
    (event) => {
        event.preventDefault();

        if (sessionStorage.getItem(VITE_PRELOAD_RELOAD_FLAG)) {
            console.warn(
                "Vite preload error detected after a previous reload attempt. Skipping reload to avoid a reload loop.",
                event,
            );
            return;
        }

        sessionStorage.setItem(VITE_PRELOAD_RELOAD_FLAG, "true");
        console.warn("Vite preload error detected. Reloading page to fetch new assets...", event);
        window.location.reload();
    },
    { once: true },
);
createRoot(document.getElementById("root")!).render(
    <Provider store={store}>
        <AuthProvider>
            <AblyProvider>
                <App />
            </AblyProvider>
        </AuthProvider>
    </Provider>
);